import { Conversation, Message } from '@shared/types';
import { FileConversation } from '../../types/misc.ts';
import { QueryClient } from '@tanstack/react-query';

// Upsert a streamed Message snapshot into the ['messages', conversationId]
// React Query cache list. Extracted verbatim (#128) from the 6 byte-identical
// upsert closures across the streaming chat mutations (mesh-gen / parametric /
// upscale, each at the stream-loop site and the post-loop tail-flush site).
// Behaviour is unchanged: append when absent, replace by id when present,
// seed a single-element list when the cache is empty/undefined.
export function upsertStreamedMessage(
  queryClient: QueryClient,
  conversationId: string,
  data: Message,
) {
  queryClient.setQueryData(
    ['messages', conversationId],
    (oldMessages: Message[] | undefined) => {
      if (!oldMessages || oldMessages.length === 0) {
        return [data];
      }
      if (oldMessages.find((msg) => msg.id === data.id)) {
        return oldMessages.map((msg) => (msg.id === data.id ? data : msg));
      } else {
        return [...oldMessages, data];
      }
    },
  );
}

// Cancel in-flight ['conversation', conversationId] queries, then optimistically
// move the conversation's active leaf to `leafMessageId`. Extracted verbatim
// (#128) from the per-hook `initialize()` closures in the streaming mutations:
// mesh-gen and parametric were byte-identical (closed-over newMessageId), and
// upscale differed only in passing its streamed message id as the leaf.
export async function initializeConversationLeaf(
  queryClient: QueryClient,
  conversationId: string,
  leafMessageId: string,
) {
  await queryClient.cancelQueries({
    queryKey: ['conversation', conversationId],
  });
  // setQueriesData (prefix filter), NOT setQueryData (exact key): EditorView's
  // conversation query is keyed ['conversation', id, user?.id] (#295), so an
  // exact ['conversation', id] write lands on a phantom entry it never reads,
  // leaving current_message_leaf_id stale → the active branch collapses to the
  // user message and the assistant artifact + viewer vanish (grey-screen on a
  // fresh generation). The prefix filter updates every conversation entry for
  // this id (2- and 3-element alike).
  queryClient.setQueriesData(
    { queryKey: ['conversation', conversationId] },
    (oldConversation: Conversation | undefined) =>
      oldConversation
        ? { ...oldConversation, current_message_leaf_id: leafMessageId }
        : oldConversation,
  );
}

export function messageSentConversationUpdate(
  newMessage: Message,
  conversationId: string,
) {
  // Q&A (Ask Tom) is an inert side-channel: it must never become the
  // conversation's active leaf (issue #40). Bump updated_at so the row still
  // sorts to the top, but leave current_message_leaf_id on the generation
  // branch for qa messages.
  const isQa = newMessage.content?.kind === 'qa';
  return (
    oldConversations: Conversation[] | FileConversation[] | undefined,
  ) => {
    if (!oldConversations) return oldConversations;
    return oldConversations
      .map((conv) => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            ...(isQa ? {} : { current_message_leaf_id: newMessage.id }),
            updated_at: newMessage.created_at,
          };
        }
        return conv;
      })
      .sort((a: Conversation, b: Conversation) => {
        return (
          new Date(b.updated_at ?? '').getTime() -
          new Date(a.updated_at ?? '').getTime()
        );
      });
  };
}

export function messageInsertedConversationUpdate(
  queryClient: QueryClient,
  newMessage: Message,
  conversationId: string,
) {
  // Q&A (Ask Tom) is an inert side-channel: it must never become the
  // conversation's active leaf, or Tree.getPath() walks off the generation
  // branch and collapses the visible chat + blanks the viewer (issue #40).
  const isQa = newMessage.content?.kind === 'qa';

  // Update the current conversation optimistically — but skip the leaf move
  // for Q&A so the generation branch stays the active path (issue #40).
  if (!isQa) {
    // Prefix filter (setQueriesData) so the leaf-advance reaches EditorView's
    // user-scoped ['conversation', id, user?.id] entry, not just the bare
    // ['conversation', id] phantom (#295 key mismatch — see
    // initializeConversationLeaf above for the full failure mode).
    queryClient.setQueriesData(
      { queryKey: ['conversation', conversationId] },
      (oldConversation: Conversation | undefined) =>
        oldConversation
          ? { ...oldConversation, current_message_leaf_id: newMessage.id }
          : oldConversation,
    );
  }

  // Update messages optimistically
  queryClient.setQueryData(
    ['messages', conversationId],
    (oldMessages: Message[] | undefined) => {
      if (!oldMessages || oldMessages.length === 0) return [newMessage];
      if (oldMessages.find((msg) => msg.id === newMessage.id)) {
        return oldMessages.map((msg) =>
          msg.id === newMessage.id ? newMessage : msg,
        );
      }
      return [...oldMessages, newMessage];
    },
  );

  // Update conversations list optimistically instead of invalidating
  queryClient.setQueryData(
    ['conversations'],
    messageSentConversationUpdate(newMessage, conversationId),
  );

  // Also update the recent conversations in sidebar
  queryClient.setQueryData(
    ['conversations', 'recent'],
    messageSentConversationUpdate(newMessage, conversationId),
  );
}
